import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';

import { LookupListsService } from '../../../core/kickstart/lookup-lists.service';
import {
  LookupListEntry,
  LookupListKind,
  LookupListVersions,
  LookupListsResponse,
} from '../../../core/kickstart/lookup-lists.types';
import { OpsContextService } from '../../../core/ops/ops-context.service';
import { IconComponent } from '../../../shared/icon/icon';

interface Tab {
  kind: LookupListKind;
  label: string;
  versionKey: keyof LookupListVersions;
  /** What an entry on this list does — the corpus's own third column. */
  effect: string;
}

/** The three lists, in the order `§B6.2`'s table states them. */
const TABS: readonly Tab[] = [
  {
    kind: 'FINANCING_WORD',
    label: 'Financing words',
    versionKey: 'financing_words',
    effect: 'Lets a quoted loan-money ground pass. Whole-word, any case.',
  },
  {
    kind: 'BANK',
    label: 'Known lenders — banks',
    versionKey: 'banks',
    effect: 'Signals a possible funder. Never passes a loan-money ground on its own.',
  },
  {
    kind: 'SPECIALIST_LENDER',
    label: 'Known lenders — specialist',
    versionKey: 'specialist_lenders',
    effect: 'Signals a possible funder, and passes a loan-money ground on the name alone.',
  },
];

/**
 * An application short_id: 8 letters or digits, case-sensitive. The form
 * checks the shape; the server checks the application exists.
 */
const SHORT_ID = /^[A-Za-z0-9]{8}$/;

/**
 * `FC-DET-060` / `§B6.2` control (1), FLOW-310: every addition AND every
 * removal names the application that prompted it. No "general experience"
 * option — a change with no application behind it is a case for the PO.
 */
const PROMPTED_BY = [Validators.required, Validators.pattern(SHORT_ID)];

/** Tiers that may add and remove (the Senior Ops floor, FLOW-304). */
const WRITE_TIERS = new Set(['SENIOR_OPS', 'CREDIT_COMMITTEE']);

/**
 * `§B6.2`'s two Ops-maintained lookup lists (FLOW-304, FC-DET-059/060).
 *
 * One page, three tabs, the whole history: live entries with who added them,
 * when, why and on which application; removed entries greyed with who removed
 * them, why, and on which application (FLOW-310). Removal is a state on the row — nothing here deletes.
 *
 * Any Authorised Ops user may read (the lists explain what a run subtracted);
 * the add and remove forms show only at Senior Ops or above, and the server
 * enforces the same floor.
 */
@Component({
  selector: 'app-ops-lookup-lists',
  imports: [
    DatePipe,
    RouterLink,
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    IconComponent,
  ],
  templateUrl: './ops-lookup-lists.html',
  styleUrl: './ops-lookup-lists.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OpsLookupListsComponent implements OnInit {
  private readonly service = inject(LookupListsService);
  private readonly opsContext = inject(OpsContextService);
  private readonly fb = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);

  protected readonly tabs = TABS;
  protected readonly selected = signal<LookupListKind>('FINANCING_WORD');
  protected readonly entries = signal<LookupListEntry[]>([]);
  protected readonly versions = signal<LookupListVersions | null>(null);
  protected readonly isLoading = signal(true);
  protected readonly loadError = signal(false);
  protected readonly isSaving = signal(false);
  protected readonly saveError = signal<string | null>(null);
  /** The row whose inline remove form is open, by short_id. */
  protected readonly removing = signal<string | null>(null);

  protected readonly canWrite = computed(() => WRITE_TIERS.has(this.opsContext.opsTier() ?? ''));

  protected readonly tab = computed(() => TABS.find((t) => t.kind === this.selected()) ?? TABS[0]);

  protected readonly active = computed(() =>
    this.entries().filter((e) => e.kind === this.selected() && e.is_active),
  );
  protected readonly removed = computed(() =>
    this.entries().filter((e) => e.kind === this.selected() && !e.is_active),
  );

  protected readonly version = computed(() => this.versions()?.[this.tab().versionKey] ?? null);

  protected readonly addForm = this.fb.nonNullable.group({
    term: ['', [Validators.required, Validators.maxLength(120)]],
    reason: ['', Validators.required],
    prompted_by: ['', PROMPTED_BY],
  });

  protected readonly removeForm = this.fb.nonNullable.group({
    reason: ['', Validators.required],
    prompted_by: ['', PROMPTED_BY],
  });

  ngOnInit(): void {
    // FLOW-309: an assessment's "Lists used" line links here with `?list=KIND`.
    const list = this.route.snapshot.queryParamMap.get('list');
    const tab = TABS.find((t) => t.kind === list);
    if (tab) {
      this.selected.set(tab.kind);
    }
    this.load();
  }

  protected select(kind: LookupListKind): void {
    this.selected.set(kind);
    this.removing.set(null);
    this.saveError.set(null);
  }

  protected reload(): void {
    this.isLoading.set(true);
    this.loadError.set(false);
    this.load();
  }

  protected countFor(kind: LookupListKind): number {
    return this.entries().filter((e) => e.kind === kind && e.is_active).length;
  }

  protected submitAdd(): void {
    if (this.addForm.invalid) {
      this.addForm.markAllAsTouched();
      return;
    }
    const { term, reason, prompted_by } = this.addForm.getRawValue();
    this.isSaving.set(true);
    this.saveError.set(null);
    this.service
      .add({
        kind: this.selected(),
        term: term.trim(),
        reason: reason.trim(),
        prompted_by: prompted_by.trim(),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.apply(res);
          this.addForm.reset();
          this.isSaving.set(false);
          this.cdr.markForCheck();
        },
        error: (err) => this.fail(err),
      });
  }

  protected openRemove(entry: LookupListEntry): void {
    this.removeForm.reset();
    this.saveError.set(null);
    this.removing.set(entry.short_id);
  }

  protected cancelRemove(): void {
    this.removing.set(null);
  }

  protected submitRemove(entry: LookupListEntry): void {
    if (this.removeForm.invalid) {
      this.removeForm.markAllAsTouched();
      return;
    }
    const { reason, prompted_by } = this.removeForm.getRawValue();
    this.isSaving.set(true);
    this.saveError.set(null);
    this.service
      .remove(entry.short_id, reason.trim(), prompted_by.trim())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.apply(res);
          this.removing.set(null);
          this.isSaving.set(false);
          this.cdr.markForCheck();
        },
        error: (err) => this.fail(err),
      });
  }

  private load(): void {
    this.service
      .list()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.apply(res);
          this.isLoading.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.isLoading.set(false);
          this.loadError.set(true);
          this.cdr.markForCheck();
        },
      });
  }

  private apply(res: LookupListsResponse): void {
    this.entries.set(res.entries);
    this.versions.set(res.versions);
  }

  private fail(err: { error?: { detail?: unknown } }): void {
    const detail = err?.error?.detail;
    this.saveError.set(
      typeof detail === 'string'
        ? detail
        : detail && typeof detail === 'object'
          ? Object.values(detail as Record<string, string[]>).flat().join(' ')
          : 'Something went wrong on our side.',
    );
    this.isSaving.set(false);
    this.cdr.markForCheck();
  }
}
